import type { ReactElement } from "react"
import styled from "styled-components"
import { StateMessage } from "~/shared/ui"
import type { DetailSession } from "../types"
import { SessionCard } from "./SessionCard"

const Root = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`

/**
 * Figma draws this as a single tab in a tab bar. One tab is not a tablist, so it
 * is the section heading with the tab's underline rule kept.
 */
const Heading = styled.h2`
  align-self: flex-start;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.color.textPrimary};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.color.textPrimary};
`

/**
 * A horizontal rail rather than a grid: Figma scrolls sessions sideways, and a
 * study's session count is small and ordered, so wrapping them into rows would
 * lose the sequence. Scroll-snap keeps a card edge aligned after a swipe or a
 * keyboard scroll.
 */
const Rail = styled.ul.attrs({ role: "list" })`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing.xxs} 0 ${theme.spacing.md}`};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  overscroll-behavior-x: contain;
`

interface SessionRailProps {
  readonly title: string
  readonly sessions: readonly DetailSession[]
  readonly emptyMessage: string
  readonly qaHook: string
}

export const SessionRail = ({ title, sessions, emptyMessage, qaHook }: SessionRailProps): ReactElement => (
  <Root aria-label={title} data-qa-hook={`${qaHook}-section`}>
    <Heading>{title}</Heading>

    {sessions.length === 0 ? (
      <StateMessage qaHook={`${qaHook}-empty`}>{emptyMessage}</StateMessage>
    ) : (
      <Rail data-qa-hook={`${qaHook}-rail`}>
        {sessions.map(session => (
          <SessionCard key={session.id} session={session} qaHook={`${qaHook}-card`} />
        ))}
      </Rail>
    )}
  </Root>
)
